import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";

const REAL_PHOTO_URL = "/images/paul-hughes.png";
const CTA_URL = "https://diyb.ca/contact-diyb/";

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

const craftSteps = [
  "I start with the frame.",
  "I check the fit.",
  "I install the hinges.",
  "I test the swing.",
  "Then I finish the trim.",
];

export default function Philosophy() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section
      id="philosophy"
      ref={ref}
      data-testid="philosophy-section"
      className="relative py-24 md:py-36 bg-[#0a192f] overflow-hidden"
    >
      <div className="blueprint-grid absolute inset-0 opacity-10" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            className="relative order-2 lg:order-1"
            style={{ y: imgY }}
          >
            <div className="relative rounded-sm overflow-hidden border border-slate-700/50 shadow-2xl shadow-black/40">
              <img
                src={REAL_PHOTO_URL}
                alt="Paul Hughes - Master Business Craftsman"
                data-testid="philosophy-photo"
                className="w-full h-auto object-cover aspect-square"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-500/60 via-transparent to-transparent" />
            </div>

            {/* Badge overlay */}
            <motion.div
              className="absolute -bottom-4 -right-4 md:bottom-6 md:right-6 bg-[#0f172a] border border-gold/30 rounded-sm px-5 py-3 shadow-xl"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <p className="font-heading font-bold text-2xl text-gold">40+</p>
              <p className="font-body text-fluid-caption text-slate-400 tracking-wider uppercase">Years Experience</p>
            </motion.div>

            {/* Decorative corners */}
            <div className="absolute -top-3 -left-3 w-10 h-10 border-t-2 border-l-2 border-gold/40" />
          </motion.div>

          {/* Text */}
          <div className="order-1 lg:order-2">
            <motion.p
              {...fadeInUp}
              transition={{ duration: 0.6 }}
              className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-4"
            >
              The Craftsman Code
            </motion.p>

            <motion.h2
              {...fadeInUp}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-heading font-bold text-fluid-h2 uppercase tracking-tight text-slate-50 mb-4"
            >
              Craft <span className="text-gold-gradient">First</span>
            </motion.h2>

            <Separator className="bg-gold/20 mb-8 max-w-xs" />

            <motion.p
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-body text-fluid-body-lg text-slate-300 mb-6"
            >
              Tools do not fix a business. A solid structure does.
              Most tech stacks fail for one simple reason &mdash;
              nobody owns the install. <span className="text-slate-50 font-semibold">I do.</span>
            </motion.p>

            <motion.p
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="font-body text-fluid-body-lg text-slate-300 mb-8"
            >
              I build like a craftsman:
            </motion.p>

            {/* Craft Steps */}
            <div className="space-y-3 mb-10">
              {craftSteps.map((step, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                >
                  <div className="w-2 h-2 rounded-full bg-gold shrink-0" />
                  <p className="font-body text-fluid-body text-slate-400">{step}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="font-body text-fluid-body text-slate-400 mb-8"
            >
              I keep the build practical. I keep the steps clear. Smart Digital
              Doorways should feel simple to the customer and steady to the owner.
            </motion.p>

            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              <a href={CTA_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="philosophy-cta-button"
                  className="bg-gold hover:bg-gold-300 text-navy-500 font-heading font-bold tracking-wider uppercase text-base px-8 py-5 rounded-sm transition-all duration-300 hover:shadow-[0_0_25px_-5px_rgba(212,175,55,0.5)]"
                >
                  Book a Conversation
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
